import React, { useState, useMemo, useEffect } from 'react';
import { Tabs, Tab } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import ArticlesGrid from './ArticlesGrid.jsx';
import ArticleEditor from './ArticleEditor.jsx';
import EtiquetaEditor from './EtiquetaEditor.jsx';
import styles from './articles.css.js';
import { saveArticle, uploadMedia, fetchArticleById, selectCurrentArticle, selectArticleStatus, clearCurrentArticle } from './ArticleSlice';
import { fetchArticleTypes, selectAllArticleTypes, selectArticleTypeDict } from './ArticleTypeSlice';

const TABS = {
    LIST: 0,
    EDITOR: 1,
};

// #region ARTICLES
const Articles = () => {
    const dispatch = useDispatch();

    const [selectedTab, setSelectedTab] = useState(TABS.LIST);
    const [currentDraft, setCurrentDraft] = useState(null); // { title, content }
    const [savedSnapshot, setSavedSnapshot] = useState(null); // last saved (draft or final)
    const [isLoadingArticle, setIsLoadingArticle] = useState(false); // flag to track if we're loading an article

    // Redux selectors for article editing
    const currentArticle = useSelector(selectCurrentArticle);
    const articleStatus = useSelector(selectArticleStatus);
    const articleTypes = useSelector(selectAllArticleTypes);
    const articleTypeDict = useSelector(selectArticleTypeDict);

    // Which editor to show when creating something new. Editing an existing
    // item ignores this — the item's own type decides (see `editorForm` below).
    const [newItemTypeId, setNewItemTypeId] = useState(null);

    // Fetch article types once on mount
    useEffect(() => {
        dispatch(fetchArticleTypes());
    }, [dispatch]);

    // Normalize currentArticle shape (id, body/content) for the editor
    const normalizedCurrentArticle = useMemo(() => {
        if (!currentArticle) return null;
        const id = currentArticle.id || currentArticle.idArticle || currentArticle.id_article;
        let content = currentArticle.content || currentArticle.body;
        return { ...currentArticle, id, content: content || null };
    }, [currentArticle]);

    // The article type in play: the loaded item's when editing, otherwise the
    // one picked for a new item. Its `form` selects the editor — 'article'
    // (Draft.js rich text, the default for every existing type) or 'etiqueta'
    // (a fielded record form).
    const activeTypeId = (normalizedCurrentArticle && normalizedCurrentArticle.idArticleType) || newItemTypeId;
    const editorForm = (articleTypeDict[activeTypeId] && articleTypeDict[activeTypeId].form) || 'article';
    const isNewItem = !normalizedCurrentArticle;

    // Determine if there are unsaved changes
    const dirty = useMemo(() => {
        if (!currentDraft) {
            return false;
        }

        if (!savedSnapshot) {
            // nothing saved yet; treat empty editor as clean
            const emptyContent = !currentDraft.title && currentDraft.content?.blocks?.every((b) => b.text === '');
            return !emptyContent;
        }

        // Check title changes
        const titleChanged = (savedSnapshot.title || '') !== (currentDraft.title || '');

        // Normalize both contents for comparison
        let savedContent = savedSnapshot.content;
        if (typeof savedContent === 'string') {
            try {
                savedContent = JSON.parse(savedContent);
            } catch (e) {
                // If parsing fails, keep as is
            }
        }

        let draftContent = currentDraft.content;
        if (typeof draftContent === 'string') {
            try {
                draftContent = JSON.parse(draftContent);
            } catch (e) {
                // If parsing fails, keep as is
            }
        }

        // Deep comparison of content
        const contentChanged = JSON.stringify(savedContent) !== JSON.stringify(draftContent);

        return titleChanged || contentChanged;
    }, [currentDraft, savedSnapshot]);

    // When loading an existing article, capture the first NON-EMPTY content from editor as baseline
    useEffect(() => {
        if (isLoadingArticle && currentDraft && !savedSnapshot) {
            // Check if content has actually loaded (not just an empty initial state)
            const hasActualContent = currentDraft.title || (currentDraft.content?.blocks && currentDraft.content.blocks.some((b) => b.text));

            if (hasActualContent) {
                // Content has loaded - use this as baseline
                setSavedSnapshot({
                    title: currentDraft.title,
                    content: currentDraft.content,
                });
                setIsLoadingArticle(false);
            }
        }
    }, [isLoadingArticle, currentDraft, savedSnapshot]);

    const handleChange = async (event, newTab) => {
        if (selectedTab === TABS.EDITOR && newTab === TABS.LIST && dirty && currentDraft) {
            // The dirty flag already handles all the change detection logic
            try {
                currentDraft.isDraft = true; // mark as draft
                const ok = await saveHandler(currentDraft);
                if (ok) setSavedSnapshot(null);
            } catch (e) {
                console.error('Auto draft save failed before leaving editor:', e);
            }
        }

        // Clear current article when leaving editor tab to ensure fresh state
        if (selectedTab === TABS.EDITOR && newTab === TABS.LIST) {
            dispatch(clearCurrentArticle());
            setCurrentDraft(null);
            setSavedSnapshot(null);
        }

        setSelectedTab(newTab);
    };

    // Handle article selection from grid
    const handleEditArticle = async (article) => {
        // Get the article ID from different possible field names
        const articleId = article.id || article.idArticle || article.id_article;

        if (articleId) {
            try {
                // Fetch the full article data including content
                const resultAction = await dispatch(fetchArticleById(articleId));

                if (fetchArticleById.fulfilled.match(resultAction)) {
                    // Clear any previous state and mark that we're loading an article
                    setCurrentDraft(null);
                    setSavedSnapshot(null);
                    setIsLoadingArticle(true);

                    // Switch to editor tab
                    // The editor will normalize and send content via onContentChange
                    // Our useEffect will capture that first update as the baseline
                    setSelectedTab(TABS.EDITOR);
                } else {
                    console.error('Failed to fetch article:', resultAction.error || resultAction.payload);
                }
            } catch (error) {
                console.error('Failed to load article:', error);
            }
        } else {
            console.error('No article ID found in:', article);
        }
    };

    async function saveHandler(data) {
        try {
            const resultAction = await dispatch(saveArticle(data));
            if (saveArticle.fulfilled.match(resultAction)) {
                setSavedSnapshot(null);
                return resultAction.payload;
            } else {
                console.error('Failed to save article:', resultAction.error);
                return false;
            }
        } catch (error) {
            console.error('Failed to save article:', error);
            return false;
        }
    }

    async function uploadHandler(file) {
        try {
            const resultAction = await dispatch(uploadMedia(file));

            if (uploadMedia.fulfilled.match(resultAction)) {
                return resultAction.payload;
            } else {
                const errorMessage = resultAction.payload || resultAction.error?.message || 'Failed to upload file';
                console.error('Failed to upload file:', errorMessage);
                // Throw the error so it can be caught by the editor
                throw new Error(errorMessage);
            }
        } catch (error) {
            console.error('Failed to upload file:', error);
            throw error; // Re-throw to be handled by the editor
        }
    }

    // #region RETURN
    return (
        <div style={styles.root}>
            <Tabs value={selectedTab} onChange={handleChange} style={styles.root.tabs.root}>
                <Tab label='List' />
                <Tab label='Editor' />
            </Tabs>
            {selectedTab === 0 && <ArticlesGrid onEditArticle={handleEditArticle} />}
            {/* Creating something new: pick what kind. Hidden while editing, when
                the item's own type already decides which editor is right. */}
            {selectedTab === 1 && isNewItem && articleTypes.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px 0', fontSize: 12, color: '#666' }}>
                    <span>Novo:</span>
                    <select
                        value={activeTypeId || ''}
                        onChange={(e) => setNewItemTypeId(Number(e.target.value))}
                        aria-label='Tipo do novo conteúdo'
                        style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #ccc', borderRadius: 4, background: '#fff' }}
                    >
                        {articleTypes.map((t) => (
                            <option key={t.idArticleType} value={t.idArticleType}>
                                {t.display_name || t.name}
                                {t.form === 'etiqueta' ? ' (ficha)' : ''}
                            </option>
                        ))}
                    </select>
                </div>
            )}
            {selectedTab === 1 && editorForm === 'etiqueta' && (
                <EtiquetaEditor
                    initialContent={normalizedCurrentArticle}
                    articleTypeId={activeTypeId}
                    onSave={saveHandler}
                    onUpload={uploadHandler}
                    onSaveSuccess={() => {
                        dispatch(clearCurrentArticle());
                        setCurrentDraft(null);
                        setSavedSnapshot(null);
                        setSelectedTab(TABS.LIST);
                    }}
                    loading={articleStatus === 'loading'}
                />
            )}
            {selectedTab === 1 && editorForm !== 'etiqueta' && (
                <ArticleEditor
                    onSave={saveHandler}
                    onUpload={uploadHandler}
                    draftInterval={20000}
                    onSaveSuccess={() => {
                        dispatch(clearCurrentArticle());
                        setCurrentDraft(null);
                        setSavedSnapshot(null);
                        setSelectedTab(TABS.LIST);
                    }}
                    onContentChange={setCurrentDraft}
                    initialContent={normalizedCurrentArticle}
                    loading={articleStatus === 'loading'}
                />
            )}
        </div>
    );
};

export default Articles;
